import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { PdfPreview } from "@/components/PdfPreview";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Loader2, AlertTriangle, XCircle, ChevronRight,
  ArrowLeft, Rocket, FileSearch, Terminal, BadgeCheck, FileText, ListChecks, ScrollText,
} from "lucide-react";

const LIVE = new Set(["running", "draft", "intake_running", "submitting"]);

// Real helper-script names per stage — the Claude-Code-style "watch it work" feed
const SCRIPT = {
  1: "sam_opportunities.py", 2: "showstopper.py", 3: "detect_forms.py",
  4: "wd_form_filler.py --map", 5: "fill_sf_form.py", 6: "readback_verify.py",
  7: "capture_outline.py", 8: "usaspending_query.py", 9: "make_proposal_pdf.py",
  10: "crosswalk.py", 11: "(human approval gate)", 12: "esign_forms.py · assemble_submission.py",
};

function StageIcon({ status }) {
  if (status === "passed") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === "running") return <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />;
  if (status === "gated") return <AlertTriangle className="h-4 w-4 text-amber-500" />;
  if (status === "failed") return <XCircle className="h-4 w-4 text-red-600" />;
  return <Circle className="h-4 w-4 text-zinc-300" />;
}

function dot(level) {
  return level === "success" ? "text-emerald-400" : level === "warning" ? "text-amber-400"
    : level === "error" ? "text-red-400" : "text-zinc-500";
}
function exitCode(level) {
  return level === "error" ? "exit 2" : level === "warning" ? "ask" : "exit 0";
}

export default function RunView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bid, setBid] = useState(null);
  const [audit, setAudit] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [selected, setSelected] = useState(null);
  const pollRef = useRef(null);
  const consoleRef = useRef(null);

  const load = useCallback(async () => {
    const [b, a] = await Promise.all([api.get(`/bids/${id}`), api.get(`/bids/${id}/audit`)]);
    setBid(b.data); setAudit(a.data);
    return b.data;
  }, [id]);

  useEffect(() => {
    let active = true;
    api.get(`/bids/${id}/compliance`).then((r) => { if (active) setCompliance(r.data.compliance || []); }).catch(() => {});
    load().then((b) => { if (active && b) setSelected((s) => s ?? (b.current_stage || 1)); });
    pollRef.current = setInterval(async () => {
      const b = await load();
      if (b && !LIVE.has(b.status)) clearInterval(pollRef.current);
    }, 1400);
    return () => { active = false; clearInterval(pollRef.current); };
  }, [load, id]);

  useEffect(() => {
    if (consoleRef.current) consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
  }, [audit]);

  if (!bid) return <div className="p-8 text-sm text-zinc-400 font-mono">Loading run…</div>;

  const stage = bid.stages.find((s) => s.num === selected) || bid.stages[0];
  const brief = (bid.stages.find((s) => s.num === 2) || {}).output || {};
  const flagged = (bid.form_fields || []).filter((f) => f.needs_human);
  const passed = (n) => (bid.stages.find((s) => s.num === n) || {}).status === "passed";
  const draftOut = (bid.stages.find((s) => s.num === 9) || {}).output || {};

  const confirm = async (decision) => {
    try {
      await api.post(`/bids/${id}/confirm`, { decision, note: "" });
      toast.success(decision === "pursue" ? "Confirmed — autopilot engaged." : "Logged as no-bid.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  return (
    <div className="p-6 w-full">
      <button onClick={() => navigate("/dashboard")} data-testid="back-to-ledger"
        className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mb-3 transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" /> Proposal Ledger
      </button>

      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display font-bold text-2xl tracking-tight text-zinc-900">{bid.solicitation_number}</h1>
            <StatusBadge status={bid.status} testid="run-status-badge" />
            {(bid.status === "running" || bid.status === "intake_running" || bid.status === "submitting") && (
              <span className="text-[11px] text-blue-600 font-mono animate-pulse">autopilot running — typically 30–90s</span>
            )}
          </div>
          <p className="text-sm text-zinc-500 mt-1">{bid.title} · {bid.agency || "—"} · Owner {bid.owner_name}</p>
        </div>
        {bid.amount && (
          <div className="text-right">
            <div className="text-[10px] tracking-wide text-zinc-500 font-semibold">Reconciled Total</div>
            <div className="font-display font-bold text-2xl tnum text-zinc-900">{bid.amount}</div>
          </div>
        )}
      </div>

      {bid.status === "awaiting_confirmation" && (
        <div data-testid="confirm-gate" className="mt-5 border border-amber-200 bg-amber-50 rounded-sm p-5 fade-in">
          <div className="flex items-center gap-2 mb-2">
            <FileSearch className="h-5 w-5 text-amber-600" />
            <div className="text-sm font-semibold text-amber-900">Confirm before autopilot — pursue this bid?</div>
            <StatusBadge status={brief.recommendation === "NO-GO" ? "failed" : "passed"} className="ml-1" />
          </div>
          <p className="text-sm text-amber-900/90 leading-relaxed">{brief.summary}</p>
          {brief.estimated_value && <p className="text-xs text-amber-800 mt-1 font-mono">Estimated magnitude: {brief.estimated_value}</p>}
          {brief.reason && <p className="text-xs text-amber-800 mt-1">Engine recommendation: {brief.recommendation} — {brief.reason}</p>}
          <div className="flex gap-3 mt-4">
            <Button data-testid="pursue-bid-button" onClick={() => confirm("pursue")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
              <Rocket className="h-4 w-4 mr-2" /> Pursue — Run Autopilot
            </Button>
            <Button data-testid="skip-bid-button" onClick={() => confirm("skip")} variant="outline" className="rounded-sm border-zinc-300 text-zinc-700 hover:bg-zinc-50">
              Skip — log no-bid & find another
            </Button>
          </div>
          <p className="text-[11px] text-amber-700/80 mt-2 font-mono">The only pause before autopilot. After you pursue, the engine runs the whole bid and stops again only at the final approval gate.</p>
        </div>
      )}

      {bid.status === "awaiting_approval" && (
        <div data-testid="approval-banner" className="mt-5 border border-amber-200 bg-amber-50 rounded-sm p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <div>
              <div className="text-sm font-semibold text-amber-900">Final approval required before send</div>
              <div className="text-xs text-amber-700">{flagged.length} field(s) need resolution. Nothing goes out until you approve.</div>
            </div>
          </div>
          <Button data-testid="go-to-approval-button" onClick={() => navigate(`/bids/${id}/approve`)} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm">
            Review & Approve <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* THREE-PANE BID CONSOLE */}
      <div className="grid grid-cols-12 gap-4 mt-5 items-stretch" style={{ minHeight: "calc(100vh - 230px)" }}>
        {/* LEFT: stage rail */}
        <div className="col-span-12 lg:col-span-3 flex flex-col">
          <div className="bg-white border border-zinc-200 rounded-sm">
            <div className="px-3 py-2.5 border-b border-zinc-200 text-[10px] tracking-[0.1em] font-semibold text-zinc-500">Workflow · 12 Stages</div>
            <div className="py-1" data-testid="stage-timeline">
              {bid.stages.map((s) => (
                <button key={s.num} data-testid={`stage-step-${s.num}`} onClick={() => setSelected(s.num)}
                  className={`w-full flex items-start gap-2.5 px-3 py-2 text-left transition-colors ${selected === s.num ? "bg-zinc-100" : "hover:bg-zinc-50"}`}>
                  <div className={`mt-0.5 ${s.status === "running" ? "rounded-full pulse-ring" : ""}`}><StageIcon status={s.status} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-[10px] text-zinc-400">{String(s.num).padStart(2, "0")}</span>
                      <span className={`text-[13px] font-medium truncate ${s.status === "pending" ? "text-zinc-400" : "text-zinc-800"}`}>{s.name}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          {/* selected stage detail */}
          <div className="bg-white border border-zinc-200 rounded-sm mt-4 p-4">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] text-zinc-400">STAGE {String(stage.num).padStart(2, "0")}</span>
              <StatusBadge status={stage.status} testid="stage-detail-status" />
            </div>
            <div className="text-sm font-semibold text-zinc-800 mt-1">{stage.name}</div>
            <p className="text-[11px] text-zinc-500 mt-2 leading-snug">{stage.gate}</p>
            {stage.evidence && <p className="text-xs text-zinc-700 font-mono mt-2 border-l-2 border-zinc-900 pl-2 leading-snug">{stage.evidence}</p>}
          </div>
        </div>

        {/* CENTER: live "watch it work" console */}
        <div className="col-span-12 lg:col-span-5 flex flex-col">
          <div className="bg-zinc-950 rounded-sm border border-zinc-800 overflow-hidden h-full flex flex-col">
            <div className="px-3 py-2 border-b border-zinc-800 flex items-center gap-2">
              <Terminal className="h-3.5 w-3.5 text-gold" />
              <span className="text-[10px] tracking-[0.16em] font-semibold text-zinc-300">Live Engine Console</span>
              <span className="ml-auto flex items-center gap-1 text-[10px] font-mono text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> watch it work
              </span>
            </div>
            <div ref={consoleRef} data-testid="bid-console" className="p-3 font-mono text-[12px] leading-relaxed overflow-auto flex-1">
              {audit.length === 0 && <div className="text-zinc-600">awaiting execution…</div>}
              {audit.map((e) => (
                <div key={e.id} className="mb-1.5">
                  {e.action === "Stage started" && (
                    <div className="text-zinc-500 mt-2">
                      <span className="text-gold">PS</span> <span className="text-zinc-400">wd\bids\{bid.solicitation_number}&gt;</span>{" "}
                      <span className="text-zinc-300">python {SCRIPT[e.stage] || "engine.py"}{e.stage ? ` --stage ${e.stage}` : ""}</span>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <span className={dot(e.level)}>●</span>
                    <span className="flex-1 text-zinc-300">
                      <span className="text-zinc-100">{e.action}</span>
                      {e.detail && <span className="text-zinc-500"> — {e.detail}</span>}
                    </span>
                    <span className={`shrink-0 ${e.level === "error" ? "text-red-400" : e.level === "warning" ? "text-amber-400" : "text-zinc-600"}`}>[{exitCode(e.level)}]</span>
                  </div>
                </div>
              ))}
              {LIVE.has(bid.status) && <div className="text-gold animate-pulse mt-1">▍</div>}
            </div>
          </div>
        </div>

        {/* RIGHT: live preview (Proposal / Forms / Crosswalk) */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white border border-zinc-200 rounded-sm h-full flex flex-col">
            <Tabs defaultValue="forms" className="w-full flex flex-col h-full">
              <div className="px-3 pt-3">
                <TabsList className="grid grid-cols-3 w-full">
                  <TabsTrigger value="proposal" data-testid="tab-proposal" className="text-xs"><ScrollText className="h-3.5 w-3.5 mr-1" /> Proposal</TabsTrigger>
                  <TabsTrigger value="forms" data-testid="tab-forms" className="text-xs"><FileText className="h-3.5 w-3.5 mr-1" /> Forms</TabsTrigger>
                  <TabsTrigger value="crosswalk" data-testid="tab-crosswalk" className="text-xs"><ListChecks className="h-3.5 w-3.5 mr-1" /> Crosswalk</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="proposal" className="p-4 flex-1 overflow-auto">
                {passed(9) ? (
                  <>
                    <Verified label="Drafted on WD template" />
                    <div className="flex flex-wrap gap-1.5 my-2">{draftOut.sections?.map((s, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 bg-zinc-100 text-zinc-600 rounded-sm">{s}</span>))}</div>
                    <p className="text-sm text-zinc-700 leading-relaxed">{bid.proposal_draft}</p>
                  </>
                ) : <Pending what="proposal draft (stage 9)" />}
              </TabsContent>

              <TabsContent value="forms" className="p-4 flex-1 overflow-auto space-y-3">
                {passed(5) ? (
                  <>
                    <Verified label={bid.status === "submitted" ? "E-signed package ready" : "Filled · read-back verified"} />
                    <PdfPreview bidId={id} kind="filled" height={560} label="SF-1442 — Ironclad filled (black ink)" />
                    {bid.status === "submitted" && <PdfPreview bidId={id} kind="signed" height={560} label="SF-1442 — Ironclad e-signed" />}
                  </>
                ) : <Pending what="form fill (stage 5)" />}
              </TabsContent>

              <TabsContent value="crosswalk" className="p-4 flex-1 overflow-auto">
                {passed(10) ? (
                  <>
                    <Verified label="0 GAP — every requirement covered" />
                    <div className="border border-zinc-200 rounded-sm overflow-hidden mt-2">
                      <table className="w-full text-sm"><tbody>
                        {compliance.map((r, i) => (
                          <tr key={i} className="border-b border-zinc-100 last:border-0">
                            <td className="px-3 py-1.5 text-zinc-700">{r.requirement}</td>
                            <td className="px-3 py-1.5"><StatusBadge status={r.status === "Covered" ? "passed" : "unmapped"} /></td>
                          </tr>
                        ))}
                      </tbody></table>
                    </div>
                  </>
                ) : <Pending what="compliance crosswalk (stage 10)" />}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}

function Verified({ label }) {
  return (
    <div className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1">
      <BadgeCheck className="h-3.5 w-3.5" /> {label}
    </div>
  );
}
function Pending({ what }) {
  return <div className="text-sm text-zinc-400 py-10 text-center">Builds here once the engine clears the {what}.</div>;
}
